<script setup lang="ts">
/**
 * BillingTrialBanner — persistent banner for billing state notifications.
 * Tech-Spec: Stripe Self-Serve Billing — Task 22
 *
 * Shows different banners based on billing state:
 * - Trial warning (admin): yellow — 7 days or less remaining
 * - Trial expired / soft-locked (admin): red — CTA to billing page
 * - Payment failed (admin): red — CTA to update payment
 * - Soft-locked (non-admin): gray — contact admin message
 */
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useRoles } from "@/composables/useRoles";
import { useBillingStore } from "@/stores/billing";

const router = useRouter();
const roles = useRoles();
const billing = useBillingStore();

const dismissed = ref(false);
const DISMISS_KEY = "pando:billing:banner:dismissed";

onMounted(async () => {
  // Load billing status for all users (admins get full status, non-admins get soft-lock state)
  await billing.loadBillingStatus();
  // Check sessionStorage for dismissal
  dismissed.value = sessionStorage.getItem(DISMISS_KEY) === "true";
});

const showBanner = computed(() => {
  if (!billing.subscription) return false;
  // Pando customers get Aspen for free — no trial/paywall banners at all.
  if (billing.billingWaived) return false;
  // Show for admins: warning, expired, past_due
  if (roles.isAdmin.value) {
    if (billing.isTrialWarning && !dismissed.value) return true;
    if (billing.isSoftLocked) return true;
    if (billing.status === "past_due") return true;
  }
  // Show for non-admins: soft-locked state only
  if (!roles.isAdmin.value && billing.isSoftLocked) return true;
  return false;
});

const bannerType = computed<"warning" | "error" | "info">(() => {
  if (billing.status === "past_due") return "error";
  if (billing.isSoftLocked) return "error";
  if (billing.isTrialWarning) return "warning";
  return "info";
});

const bannerMessage = computed(() => {
  if (!roles.isAdmin.value && billing.isSoftLocked) {
    return "Your account is in read-only mode. Contact your administrator to restore full access.";
  }
  if (billing.status === "past_due") {
    return "Your last payment failed. Please update your payment method to avoid service interruption.";
  }
  if (billing.isTrialExpired || billing.status === "trial_expired") {
    return "Your free trial has ended. Your account is in read-only mode. Add a payment method to restore full access.";
  }
  if (billing.status === "canceled") {
    return "Your subscription has been canceled. Your account is in read-only mode. Re-subscribe to restore full access.";
  }
  if (billing.isTrialWarning) {
    return `Your 30-day free trial ends in ${billing.trialDaysRemaining} day${billing.trialDaysRemaining !== 1 ? "s" : ""}. Add a payment method to continue using Aspen.`;
  }
  return "";
});

const ctaText = computed(() => {
  if (!roles.isAdmin.value) return "";
  if (billing.status === "past_due") return "Update payment";
  if (billing.isTrialExpired || billing.status === "trial_expired")
    return "Add payment method";
  if (billing.status === "canceled") return "Re-subscribe";
  if (billing.isTrialWarning) return "Set up billing";
  return "";
});

const canDismiss = computed(() => {
  // Only the trial warning is dismissable (not expired/failed)
  return roles.isAdmin.value && billing.isTrialWarning && !billing.isSoftLocked;
});

function goToBilling() {
  router.push("/admin/billing");
}

function dismiss() {
  dismissed.value = true;
  sessionStorage.setItem(DISMISS_KEY, "true");
}

const bgClass = computed(() => {
  if (bannerType.value === "error")
    return "bg-red-50 border-red-200 text-red-800";
  if (bannerType.value === "warning")
    return "bg-amber-50 border-amber-200 text-amber-800";
  return "bg-gray-50 border-gray-200 text-gray-700";
});

const buttonClass = computed(() => {
  if (bannerType.value === "error")
    return "bg-red-600 hover:bg-red-700 text-white";
  return "bg-amber-600 hover:bg-amber-700 text-white";
});
</script>

<template>
  <div
    v-if="showBanner"
    :class="[bgClass, 'w-full border-b px-4 py-3']"
    role="alert"
  >
    <div
      class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
    >
      <p class="text-sm font-medium">{{ bannerMessage }}</p>
      <div class="flex items-center gap-2 shrink-0">
        <button
          v-if="ctaText && roles.isAdmin.value"
          :class="[
            buttonClass,
            'rounded-md px-3 py-1.5 text-sm font-semibold transition-colors',
          ]"
          @click="goToBilling"
        >
          {{ ctaText }} &rarr;
        </button>
        <button
          v-if="canDismiss"
          class="ml-2 text-current opacity-60 hover:opacity-100 transition-opacity"
          aria-label="Dismiss"
          @click="dismiss"
        >
          &times;
        </button>
      </div>
    </div>
  </div>
</template>
